import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Navbar, Nav, Dropdown } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Images } from '../../../assets/assets';
import SvgIcon from '../SvgIcon/SvgIcon';
import authService from '../../../services/authService';
import profileService from '../../../services/profileService';
import './Header.css';

const tabs = [
    { to: '/', label: 'Dashboard', Icon: Images.DashboardIcon, ActiveIcon: Images.DashboardIconFill },
    { to: '/analysis', label: 'AI Analysis', Icon: Images.AnalysisIcon },
    { to: '/progression', label: 'Progression', Icon: Images.ProgressionIcon },
    { to: '/insights', label: 'AI Insights', Icon: Images.InsightsIcon },
    { to: '/biomarkers', label: 'Biomarkers', Icon: Images.InsightsIcon },
    { to: '/reports', label: 'Reports', Icon: Images.ReportsIcon },
];

export default function Header() {
    const navigate = useNavigate();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [userInfo, setUserInfo] = useState({
        name: 'Medical User',
        email: 'user@example.com'
    });
    const [profilePicture, setProfilePicture] = useState(null);

    useEffect(() => {
        // Get user info from localStorage or API
        const user = authService.getCurrentUser();
        if (user) {
            // Try to get user data from localStorage or make API call
            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const parsedData = JSON.parse(userData);
                    setUserInfo({
                        name: parsedData.username || 'Medical User',
                        email: parsedData.email || 'user@example.com'
                    });
                } catch (error) {
                    console.error('Error parsing user data:', error);
                }
            }
        }
        
        // Fetch profile picture
        const fetchProfilePicture = async () => {
            try {
                const response = await profileService.getProfile();
                if (response.success && response.data && response.data.profilePicture) {
                    const imageUrl = response.data.profilePicture;
                    setProfilePicture(imageUrl);
                }
            } catch (error) {
                console.error('Error fetching profile picture:', error);
                // Keep default static image on error
            }
        };
        
        fetchProfilePicture();
    }, []);

    // Check for selected patient data
    useEffect(() => {
        const checkPatientData = () => {
            const patientData = sessionStorage.getItem('selectedPatient');
            if (patientData) {
                try {
                    const patient = JSON.parse(patientData);
                    setSelectedPatient(patient);
                } catch (error) {
                    console.error('Error parsing patient data:', error);
                    setSelectedPatient(null);
                }
            } else {
                setSelectedPatient(null);
            }
        };

        // Check initially
        checkPatientData();

        // Listen for custom events
        const handlePatientSelected = () => {
            checkPatientData();
        };

        const handleLogout = () => {
            setSelectedPatient(null);
        };

        // Listen for custom events
        window.addEventListener('patientSelected', handlePatientSelected);
        window.addEventListener('userLogout', handleLogout);
        
        // Listen for window focus (when user comes back to tab)
        const handleFocus = () => {
            checkPatientData();
        };
        window.addEventListener('focus', handleFocus);

        // Also check periodically (every 500ms) to catch any missed updates
        const interval = setInterval(checkPatientData, 500);

        // Listen for profile picture updates
        const handleProfileUpdate = async () => {
            try {
                const response = await profileService.getProfile();
                if (response.success && response.data && response.data.profilePicture) {
                    const imageUrl = response.data.profilePicture;
                    setProfilePicture(imageUrl);
                }
            } catch (error) {
                console.error('Error fetching updated profile picture:', error);
            }
        };
        
        window.addEventListener('profileUpdated', handleProfileUpdate);

        return () => {
            window.removeEventListener('patientSelected', handlePatientSelected);
            window.removeEventListener('userLogout', handleLogout);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('profileUpdated', handleProfileUpdate);
            clearInterval(interval);
        };
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await authService.logout();
            // Clear sessionStorage and dispatch logout event
            sessionStorage.removeItem('selectedPatient');
            window.dispatchEvent(new CustomEvent('userLogout'));
            toast.success('Logged out successfully');
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails on server, clear local token and redirect
            localStorage.removeItem('accessToken');
            sessionStorage.removeItem('selectedPatient');
            window.dispatchEvent(new CustomEvent('userLogout'));
            navigate('/login', { replace: true });
        } finally {
            setIsLoggingOut(false);
        }
    };

    return (
        <>
            <header className='header'>
                <div className='nav-top'>
                    <Link to="/" className="brand-logo">
                        <img src={Images.SiteLogo} alt="Logo" className="img-fluid" />
                    </Link>
                    <Dropdown className="user-dropdown">
                        <Dropdown.Toggle 
                            as="div" 
                            className="user-avatar" 
                            aria-label="User menu"
                            style={{ cursor: 'pointer' }}
                        >
                            <img 
                                src={profilePicture || Images.UserProfile} 
                                alt="User" 
                                className="img-fluid rounded-circle" 
                                width="45" 
                                height="45"
                                onError={(e) => {
                                    // Fallback to static image if profile picture fails to load
                                    e.target.src = Images.UserProfile;
                                }}
                            />
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="user-menu">
                            <Dropdown.ItemText className="user-info">
                                <div className="user-name">{userInfo.name}</div>
                                <div className="user-email">{userInfo.email}</div>
                            </Dropdown.ItemText>
                            <Dropdown.Divider />
                            <Dropdown.Item as={Link} to="/profile">
                                Profile
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item onClick={handleLogout} disabled={isLoggingOut}>
                                {isLoggingOut ? 'Logging out...' : 'Logout'}
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="nav-bottom">
                    <Navbar bg="white" className="navbar" expand="md">
                        <Nav variant="pills" className="gap-2 flex-wrap">
                            {tabs.map((t) => {
                                // Check if tab should be disabled
                                const isDisabled = !selectedPatient && t.to !== '/';
                                const isAnalysisTab = t.to === '/analysis';
                                
                                return (
                                    <NavLink
                                        key={t.to}
                                        to={isDisabled ? '#' : t.to}
                                        end={t.to === '/'}
                                        className={({ isActive }) => 
                                            'nav-link' + 
                                            (isActive ? ' active' : '') + 
                                            (isDisabled ? ' disabled' : '')
                                        }
                                        onClick={(e) => {
                                            if (isDisabled) {
                                                e.preventDefault();
                                                if (isAnalysisTab) {
                                                    alert('Please select your profile first by clicking the eye icon in the Patient Table.');
                                                } else {
                                                    alert('Please select your profile first to access this feature.');
                                                }
                                            }
                                        }}
                                    >
                                        {({ isActive }) => (
                                            <>
                                                {t.Icon ? (
                                                    <SvgIcon
                                                        src={isActive ? t.ActiveIcon || t.Icon : t.Icon}
                                                        className="nav-icon"
                                                        data-default={t.Icon}
                                                        data-active={t.ActiveIcon || t.Icon}
                                                    />
                                                ) : null}
                                                {t.label}
                                                {isDisabled && <span className="disabled-indicator">🔒</span>}
                                            </>
                                        )}
                                    </NavLink>
                                );
                            })}
                        </Nav>
                    </Navbar>
                </div>
            </header >
        </>
    );
}

